// /app/frontend/src/components/compliance/map/ImportFromROPAModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { 
  FileText, Search, Import, AlertCircle, CheckCircle2,
  Database, Globe, Building2, Cpu
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { ropaAPI, entitiesAPI } from '../../../services/api';
import { toast } from 'sonner';

// Map ROPA record to data flow nodes
const mapROPAToNodes = (ropa, entityName) => {
  const nodes = [];
  const edges = [];
  const baseX = 300;
  const baseY = 100;
  let yOffset = 0;
  
  // Create main processing node from ROPA
  const processNodeId = `ropa-process-${ropa.id}`;
  nodes.push({
    id: processNodeId,
    type: 'entityNode',
    position: { x: baseX + 300, y: baseY + yOffset },
    data: {
      id: processNodeId,
      type: 'process',
      label: ropa.process_name,
      description: ropa.purpose,
      risk: ropa.collects_sensitive_data ? 'high' : 'medium',
      dataCategories: [...(ropa.personal_data_types || []), ...(ropa.sensitive_data_types || [])],
      legalBasis: ropa.legal_basis,
      purpose: ropa.purpose,
      retention: ropa.retention_period,
      securityMeasures: ropa.security_measures || [],
      ropaId: ropa.id,
      entityName: entityName,
    },
  });
  
  // Create data subjects node
  if (ropa.data_subjects && ropa.data_subjects.length > 0) {
    const subjectsNodeId = `ropa-subjects-${ropa.id}`;
    nodes.push({
      id: subjectsNodeId,
      type: 'entityNode',
      position: { x: baseX - 200, y: baseY + yOffset },
      data: {
        id: subjectsNodeId,
        type: 'subjects',
        label: ropa.data_subjects.join(', '),
        description: `Data subjects for ${ropa.process_name}`,
        risk: 'low',
        dataCategories: ropa.personal_data_types || [],
      },
    });
    edges.push({
      id: `e-subjects-${ropa.id}`,
      source: subjectsNodeId,
      target: processNodeId,
      type: 'dataFlowEdge',
      data: { label: 'Personal Data' },
    });
    yOffset += 100;
  }
  
  // Create storage node if system is used
  if (ropa.uses_systems && ropa.system_name) {
    const storeNodeId = `ropa-store-${ropa.id}`;
    nodes.push({
      id: storeNodeId,
      type: 'entityNode',
      position: { x: baseX + 600, y: baseY },
      data: {
        id: storeNodeId,
        type: 'store',
        label: ropa.system_name,
        description: `${ropa.system_location === 'cloud' ? 'Cloud' : 'Internal'} storage system`,
        risk: ropa.system_location === 'cloud' ? 'medium' : 'low',
        dataCategories: ropa.personal_data_types || [],
        securityMeasures: ropa.electronic_security_measures || [],
      },
    });
    edges.push({
      id: `e-store-${ropa.id}`,
      source: processNodeId,
      target: storeNodeId,
      type: 'dataFlowEdge',
      data: { label: 'Storage' },
    });
  }
  
  // Create external sharing node if applicable
  if (ropa.shares_externally && ropa.external_sharing_recipients) {
    const thirdPartyId = `ropa-thirdparty-${ropa.id}`;
    nodes.push({
      id: thirdPartyId,
      type: 'entityNode',
      position: { x: baseX + 600, y: baseY + 150 },
      data: {
        id: thirdPartyId,
        type: 'thirdparty',
        label: ropa.external_sharing_recipients.substring(0, 50),
        description: `External data sharing recipient`,
        risk: ropa.has_data_sharing_agreement ? 'medium' : 'high',
        dataCategories: ropa.data_categories || [],
      },
    });
    edges.push({
      id: `e-thirdparty-${ropa.id}`,
      source: processNodeId,
      target: thirdPartyId,
      type: 'dataFlowEdge',
      data: { label: 'Sharing' },
    });
  }
  
  // Create cross-border transfer node if applicable
  if (ropa.transfers_outside_country || (ropa.system_location === 'cloud' && ropa.cloud_country)) {
    const transferId = `ropa-transfer-${ropa.id}`;
    const country = ropa.cloud_country || 'International';
    nodes.push({
      id: transferId,
      type: 'entityNode',
      position: { x: baseX + 900, y: baseY + 75 },
      data: {
        id: transferId,
        type: 'transfer',
        label: `Transfer to ${country}`,
        description: `Cross-border data transfer`,
        risk: ropa.transfer_safeguards ? 'medium' : 'high',
        country: country,
        safeguards: ropa.transfer_safeguards,
        hasAdequacy: false, // Would need to be determined
        dataCategories: ropa.personal_data_types || [],
      },
    });
    edges.push({
      id: `e-transfer-${ropa.id}`,
      source: processNodeId,
      target: transferId,
      type: 'dataFlowEdge',
      data: { label: 'Transfer', dashed: true },
    });
  }
  
  return { nodes, edges };
};

const ImportFromROPAModal = ({ open, onOpenChange, onImport, existingNodeIds = [] }) => {
  const [ropaRecords, setRopaRecords] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ropaRes, entitiesRes] = await Promise.all([
        ropaAPI.getAll(),
        entitiesAPI.getAll()
      ]);
      setRopaRecords(ropaRes.data);
      setEntities(entitiesRes.data);
    } catch (error) {
      toast.error('Failed to load ROPA records');
    } finally {
      setLoading(false);
    }
  };

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity?.entity_name || 'Unknown Entity';
  };

  const toggleRecord = (recordId) => {
    setSelectedRecords(prev => 
      prev.includes(recordId) 
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };

  const selectAll = () => {
    const filtered = filteredRecords.map(r => r.id);
    setSelectedRecords(prev => {
      const allSelected = filtered.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !filtered.includes(id));
      }
      return [...new Set([...prev, ...filtered])];
    });
  };

  const handleImport = () => {
    const selectedData = ropaRecords.filter(r => selectedRecords.includes(r.id));
    
    let allNodes = [];
    let allEdges = [];
    let offsetY = 0;
    
    selectedData.forEach((ropa, index) => {
      const entityName = getEntityName(ropa.organization_id);
      const { nodes, edges } = mapROPAToNodes(ropa, entityName);
      
      // Offset each ROPA's nodes to prevent overlap
      const offsetNodes = nodes.map(node => ({
        ...node,
        position: {
          x: node.position.x,
          y: node.position.y + offsetY
        }
      }));
      
      allNodes = [...allNodes, ...offsetNodes];
      allEdges = [...allEdges, ...edges];
      offsetY += 300; // Space between ROPA imports
    });
    
    onImport(allNodes, allEdges);
    toast.success(`Imported ${selectedRecords.length} ROPA record(s) as ${allNodes.length} nodes`);
    onOpenChange(false);
    setSelectedRecords([]);
  };

  const filteredRecords = ropaRecords.filter(ropa => {
    const matchesSearch = ropa.process_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          ropa.purpose.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEntity = entityFilter === 'all' || ropa.organization_id === entityFilter;
    return matchesSearch && matchesEntity;
  });

  const alreadyImported = (ropaId) => {
    return existingNodeIds.some(id => id.includes(ropaId));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col bg-slate-950 border-slate-800">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FileText className="h-5 w-5 text-violet-400" />
            Import from ROPA Records
          </DialogTitle>
          <DialogDescription>
            Select ROPA records to automatically generate data flow nodes. Each record will create processing, storage, and transfer nodes based on its data.
          </DialogDescription>
        </DialogHeader>
        
        {/* Filters */}
        <div className="flex gap-3 py-3 border-b border-slate-800">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search processes..."
              className="pl-9 bg-slate-900 border-slate-800"
            />
          </div>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-[200px] bg-slate-900 border-slate-800">
              <SelectValue placeholder="All entities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              {entities.map((entity) => (
                <SelectItem key={entity.id} value={entity.id}>
                  {entity.entity_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Records List */}
        <div className="flex-1 overflow-y-auto py-2 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No ROPA records found</p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="flex items-center gap-2 p-2 bg-slate-900/50 rounded-lg">
                <Checkbox
                  checked={filteredRecords.length > 0 && filteredRecords.every(r => selectedRecords.includes(r.id))}
                  onCheckedChange={selectAll}
                />
                <span className="text-sm text-slate-400">
                  Select all ({filteredRecords.length} records)
                </span>
              </div>
              
              {filteredRecords.map((ropa) => {
                const isImported = alreadyImported(ropa.id);
                const isSelected = selectedRecords.includes(ropa.id);
                
                return (
                  <div
                    key={ropa.id}
                    className={cn(
                      "p-4 rounded-lg border transition-all cursor-pointer",
                      isImported 
                        ? "border-slate-800 bg-slate-900/30 opacity-60"
                        : isSelected
                          ? "border-violet-500 bg-violet-500/10"
                          : "border-slate-800 hover:border-slate-700 bg-slate-900/50"
                    )}
                    onClick={() => !isImported && toggleRecord(ropa.id)}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={isSelected}
                        disabled={isImported}
                        onCheckedChange={() => !isImported && toggleRecord(ropa.id)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-white truncate">{ropa.process_name}</h4>
                          {isImported && (
                            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Imported
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-400 line-clamp-1 mb-2">{ropa.purpose}</p>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="text-xs bg-slate-800/50">
                            {getEntityName(ropa.organization_id)}
                          </Badge>
                          <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/20">
                            {ropa.legal_basis}
                          </Badge>
                          {ropa.collects_sensitive_data && (
                            <Badge variant="outline" className="text-xs bg-rose-500/10 text-rose-400 border-rose-500/20">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Sensitive Data
                            </Badge>
                          )}
                          {ropa.transfers_outside_country && (
                            <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/20">
                              <Globe className="h-3 w-3 mr-1" />
                              Cross-Border
                            </Badge>
                          )}
                        </div>
                        
                        {/* Preview of what will be created */}
                        {isSelected && (
                          <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-4 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Cpu className="h-3 w-3 text-purple-400" />
                              Processing node
                            </span>
                            {ropa.uses_systems && (
                              <span className="flex items-center gap-1">
                                <Database className="h-3 w-3 text-orange-400" />
                                Storage node
                              </span>
                            )}
                            {ropa.shares_externally && (
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3 text-cyan-400" />
                                Third party node
                              </span>
                            )}
                            {ropa.transfers_outside_country && (
                              <span className="flex items-center gap-1">
                                <Globe className="h-3 w-3 text-rose-400" />
                                Transfer node
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <DialogFooter className="border-t border-slate-800 pt-4">
          <div className="flex items-center justify-between w-full">
            <span className="text-sm text-slate-400">
              {selectedRecords.length} record{selectedRecords.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleImport}
                disabled={selectedRecords.length === 0}
                className="bg-violet-600 hover:bg-violet-700"
              >
                <Import className="h-4 w-4 mr-2" />
                Import Selected
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFromROPAModal;
